import React, { useEffect, useRef, useState } from "react";
import Draggable from "react-draggable";
import ReactSelect from "react-select";
import Portal from "../../common/components/Portal";
import ReactTooltip from "react-tooltip";
import ChangeConfirmationModal from "../../common/components/ChangeConfirmationModal";
import ConfirmationModal from "../../common/components/ConfirmationModal";
import { sanitizeHtml } from "../../common/components/SafeHtml";
import { ERROR_COPY } from "../../../config/errorMessages";

const SearchAndReplace = ({
    onCancel,
    handleSave,
    tableSearch,
    tableFields,
    loading,
    setFindParams,
    findBeforeReplace,
    replaceCountResponse,
    replaceSuccessResponse,
    setAlertMessage,
    entityLabel = "asset",
    entityPluralLabel = "assets",
    showFindTypeSelection = false,
    isMobileAsset = false,
    isBgTask = false
}) => {
    const initialValues = {
        search: "",
        replace: "",
        match_entire_cell: false,
        match_case: false,
        replace_fields: [],
        search_operator: "OR"
    };
    const capitalize = label => (label ? `${label?.charAt(0)?.toUpperCase()}${label?.slice(1)}` : label);
    const entityLabelCaps = capitalize(entityLabel);
    const entityPluralLabelCaps = capitalize(entityPluralLabel);
    const [data, setData] = useState(initialValues);
    const [errorMessage, setErrorMessage] = useState("");
    const [errorBoundary, setErrorBoundary] = useState(false);
    const [findType, setFindType] = useState("all");
    const [showTypeSelect, setShowTypeSelect] = useState(false);
    const [showConfirmReplaceModal, setShowConfirmReplaceModal] = useState(false);
    const [isDeleteModal, setIsDeleteModal] = useState(false);
    const [showOnReplaceModal, setShowOnReplaceodal] = useState(false);
    const searchAndReplaceRef = useRef(null);

    useEffect(() => {
        return () => {
            setData(initialValues);
        };
    }, []);

    useEffect(() => {
        if (replaceSuccessResponse && searchAndReplaceRef?.current) {
            searchAndReplaceRef.current = false;
            const { success, error, detail = null } = replaceSuccessResponse;
            if (success) {
                setShowOnReplaceodal(true);
                setIsDeleteModal(false);
            } else if (error) {
                setAlertMessage(typeof detail === "string" ? detail : ERROR_COPY.GENERIC);
            }
        }
    }, [replaceSuccessResponse]);

    const cases = [
        { value: "sentence_case", label: "Sentence case" },
        { value: "upper_case", label: "Upper Case" },
        { value: "lower_case", label: "Lower Case" },
        { value: "capitalized_case", label: "Capitalized Case" }
    ];
    const tableSearchKey = tableSearch
        ? JSON.stringify([
              tableSearch?.find,
              tableSearch?.match_case,
              tableSearch?.match_entire_cell,
              tableSearch?.replace_fields,
              tableSearch?.search_operator
          ])
        : null;

    useEffect(() => {
        if (tableSearch) {
            setData(prevData => ({
                ...prevData,
                search: tableSearch?.find,
                replace_fields: (tableFields ?? [])?.filter(e => tableSearch?.replace_fields?.some(i => i === e?.value)),
                match_case: tableSearch?.match_case,
                match_entire_cell: tableSearch?.match_entire_cell,
                search_operator: tableSearch?.search_operator ?? prevData?.search_operator ?? "OR"
            }));
        }
    }, [tableSearchKey, tableFields?.length]);

    const onChange = (name, value) => {
        if (errorBoundary) validateData({ ...data, [name]: value });
        setData(prevData => ({ ...prevData, [name]: value }));
    };

    const validateData = onChangeData => {
        const dataToBeValidated = onChangeData ? onChangeData : data;
        let hasError = false;
        let error = "";
        Object.entries(dataToBeValidated).forEach(([key, value]) => {
            if (!hasError)
                if (key === "search" && !value?.length) {
                    hasError = true;
                    error = "Find value is required";
                } else if (key === "replace" && !value?.length) {
                    hasError = true;
                    error = "Replace value is required";
                } else if (key === "replace_fields" && !value?.length) {
                    hasError = true;
                    error = "Please select at least one field to replace";
                }
        });
        setErrorBoundary(hasError);
        setErrorMessage(error);
        return hasError;
    };

    const hasMultipleFields = (data?.replace_fields?.length ?? 0) > 1;
    const searchOperatorOptions = [
        { value: "OR", label: "OR" },
        { value: "AND", label: "AND" }
    ];

    const filterOption = (option, inputValue) => {
        return option.label.toLowerCase().includes(inputValue.toLowerCase());
    };

    const applyFindParams = () => {
        onCancel();
        setFindParams(prevParams => ({
            ...prevParams,
            findParams: {
                find: data?.search,
                replace_fields: data?.replace_fields?.map(e => e?.value),
                findType: findType,
                match_case: data?.match_case,
                match_entire_cell: data?.match_entire_cell,
                search_operator: data?.search_operator ?? "OR"
            }
        }));
    };

    const renderOnReplaceConfirm = () => {
        if (!showOnReplaceModal) return null;

        const { updated_records_count = 0, result = {} } = replaceSuccessResponse ?? {};

        return (
            <Portal
                body={
                    <ConfirmationModal
                        singleOk
                        isSurveyAsset
                        heading={
                            isBgTask ? (
                                <p>Background task initiated successfully</p>
                            ) : (
                                <p>
                                    Successfully updated! A total of <span className="count">{updated_records_count}</span> {entityPluralLabel} have
                                    been changed, including{" "}
                                    {Object.entries(result).map(([key, value], index, array) => (
                                        <span key={key}>
                                            <span className="count">{value}</span> {key}
                                            {value !== 1 ? "s" : ""}
                                            {index < array.length - 1 ? ", " : "."}
                                        </span>
                                    ))}
                                </p>
                            )
                        }
                        onlyOk={() => {
                            onCancel();
                            setShowOnReplaceodal(false);
                        }}
                    />
                }
                onCancel={() => {
                    onCancel();
                    setShowOnReplaceodal(false);
                }}
            />
        );
    };

    const renderConfirmationReplaceModal = () => {
        if (!showConfirmReplaceModal) return null;

        const formattedFields =
            data?.replace_fields.length > 1
                ? data?.replace_fields
                      ?.map(e => e?.label)
                      .slice(0, -1)
                      .join(", ") +
                  " and " +
                  data?.replace_fields?.map(e => e?.label)[data?.replace_fields.length - 1]
                : data?.replace_fields?.map(e => e?.label)[0];

        const convertedCount = replaceCountResponse?.skipped_converted_count ?? 0;
        const convertedText = convertedCount ? (
            <>
                <span className="badge-red-circled search-replace-badge">{convertedCount}</span>{" "}
                {convertedCount === 1 ? entityLabelCaps : entityPluralLabelCaps} in the selected scope {convertedCount === 1 ? "has" : "have"} already
                been converted and will be skipped from this update.
            </>
        ) : null;
        const skippedVerbCount = replaceCountResponse?.skipped_verb_count ?? 0;
        const skippedVerbText = skippedVerbCount ? (
            <>
                <span className="badge-red-circled search-replace-badge">{skippedVerbCount}</span>{" "}
                {skippedVerbCount === 1 ? entityLabelCaps : entityPluralLabelCaps} will be skipped because their Recommendation field must start with
                an approved verb.
            </>
        ) : null;
        const noteItems = [convertedText, skippedVerbText].filter(Boolean);

        const assetEntries = Object.entries(replaceCountResponse?.updating_field_count ?? {}).map(([key, value], index, array) => (
            <span key={key}>
                {array?.length > 1 && index === array.length - 1 ? " and " : ""}
                <span className="badge-red-circled search-replace-badge">{value}</span> {key} field
                {array?.length > 2 && index !== array.length - 1 ? "," : ""}
            </span>
        ));
        return (
            <Portal
                body={
                    replaceCountResponse?.updating_records_count === 0 ? (
                        <ConfirmationModal
                            isNotOk
                            singleOk
                            isSurveyAsset
                            heading={<p>No {entityPluralLabel} found to be changed.</p>}
                            message={
                                noteItems.length ? (
                                    <>
                                        <strong>{noteItems.length > 1 ? "Notes:" : "Note:"}</strong>{" "}
                                        {noteItems.map((item, index) => (
                                            <React.Fragment key={index}>
                                                {index ? <br /> : null}
                                                {item}
                                            </React.Fragment>
                                        ))}
                                    </>
                                ) : null
                            }
                            onlyOk={() => {
                                setShowConfirmReplaceModal(false);
                                setIsDeleteModal(false);
                            }}
                        />
                    ) : (
                        <ChangeConfirmationModal
                            heading={
                                <>
                                    <div className="search-replace-confirmation__heading mb-2">
                                        Confirm the value {isDeleteModal ? "Removal" : "Replacement"}
                                    </div>
                                    <div className="search-replace-confirmation text-left ml-3 mb-2">
                                        <h4 className="search-replace-confirmation__line mb-2 mt-0">
                                            Do you want to {isDeleteModal ? "remove" : "replace"} the value:{" "}
                                            <b className="fw-medium search-replace-confirmation__value">"{data?.search}"</b>
                                        </h4>
                                        <h4 className="search-replace-confirmation__line mb-2 mt-0">
                                            In the selected {data?.replace_fields.length > (1)?.length > 1 ? "fields" : "field"}:{" "}
                                            <b className="fw-medium search-replace-confirmation__value">{formattedFields}</b>
                                        </h4>
                                        {isDeleteModal ? null : (
                                            <h4 className="search-replace-confirmation__line mb-0 mt-0">
                                                With the new value: <b className="fw-medium search-replace-confirmation__value">"{data?.replace}"</b>
                                            </h4>
                                        )}
                                    </div>
                                </>
                            }
                            paragraph={
                                <>
                                    <div>
                                        The values of the selected fields will be {isDeleteModal ? "removed" : "replaced"}{" "}
                                        {data?.replace?.trim() ? `with "${data?.replace}"` : ""} . This change will update {assetEntries}.
                                    </div>
                                    {noteItems.length === 1 ? (
                                        <div className="mt-2">
                                            <strong>Note:</strong> {noteItems[0]}
                                        </div>
                                    ) : noteItems.length > 1 ? (
                                        <div className="search-replace-note mt-2">
                                            <strong>Notes:</strong>
                                            <ul className="search-replace-note__list">
                                                {noteItems.map((item, index) => (
                                                    <li key={index}>{item}</li>
                                                ))}
                                            </ul>
                                        </div>
                                    ) : null}

                                    <div className="mt-2">
                                        <b> Are you sure you want to continue?</b>
                                    </div>
                                </>
                            }
                            onCancel={() => {
                                setShowConfirmReplaceModal(false);
                                setIsDeleteModal(false);
                            }}
                            onOk={() => {
                                setShowConfirmReplaceModal(false);
                                handleSave(
                                    {
                                        ...data,
                                        replace_fields: data?.replace_fields?.map(e => e?.value),
                                        replace: isDeleteModal ? "" : data?.replace
                                    },
                                    isDeleteModal
                                );
                                searchAndReplaceRef.current = true;
                            }}
                        />
                    )
                }
                onCancel={() => {
                    setShowConfirmReplaceModal(false);
                    setIsDeleteModal(false);
                }}
            />
        );
    };

    const renderConfirmationModal = () => {
        if (!showFindTypeSelection || !showTypeSelect) return null;
        return (
            <Portal
                body={
                    <div className="modal modal-region modal-view modal-assets-type" id="modalId" style={{ display: "block" }} tabIndex="-1">
                        <Draggable handle=".find-options" cancel=".not-draggable">
                            <div className="modal-dialog" role="document">
                                <div className="modal-content">
                                    <div className="modal-header find-options " style={{ cursor: "move" }}>
                                        <h4 className="modal-title" id="exampleModalLabel">
                                            <div className="txt-hed">Choose the type of assets to be found</div>
                                        </h4>
                                        <div className="not-draggable">
                                            <button type="button" className="close" onClick={() => setShowTypeSelect(false)}>
                                                <span aria-hidden="true">
                                                    <img src="/img/close.svg" alt="" />
                                                </span>
                                            </button>
                                        </div>
                                    </div>
                                    <div className="modal-body region-otr">
                                        <div className="col-md-12 formInp search mt-3">
                                            <div className="d-flex align-items-center mr-4">
                                                <input
                                                    type="radio"
                                                    id="all"
                                                    name="findType"
                                                    value={findType}
                                                    checked={findType === "all"}
                                                    onChange={e => setFindType("all")}
                                                />

                                                <label htmlFor="all" className="m-0">
                                                    All Assets
                                                </label>
                                            </div>
                                        </div>
                                        <div className="col-md-12 formInp search mt-3">
                                            <div className="d-flex align-items-center mr-4">
                                                <input
                                                    type="radio"
                                                    id="completed"
                                                    name="findType"
                                                    value={findType}
                                                    checked={findType === "completed"}
                                                    onChange={e => setFindType("completed")}
                                                />

                                                <label htmlFor="completed" className="m-0">
                                                    Production Assets
                                                </label>
                                            </div>
                                        </div>
                                        <div className="col-md-12 formInp search mt-3">
                                            <div className="d-flex align-items-center mr-4">
                                                <input
                                                    type="radio"
                                                    id="review"
                                                    name="findType"
                                                    value={findType}
                                                    checked={findType === "review"}
                                                    onChange={e => setFindType("review")}
                                                />

                                                <label htmlFor="review" className="m-0">
                                                    In Review Assets
                                                </label>
                                            </div>
                                        </div>
                                        <div class="col-md-12 p-0 mt-4 text-right  d-flex col-md-12">
                                            <div className="tot-btr">
                                                <button type="button" onClick={applyFindParams} className="btn btn-primary btnRgion btn-wid">
                                                    Ok
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </Draggable>
                    </div>
                }
                onCancel={() => setShowTypeSelect(false)}
            />
        );
    };

    return (
        <div className="modal modal-region modal-view" id="modalId" style={{ display: "block" }} tabIndex="-1">
            <Draggable handle=".draggable" cancel=".not-draggable">
                <div className="modal-dialog search-replace-modal-dialog" role="document">
                    <div className="modal-content search-replace-modal-content">
                        <ReactTooltip
                            id="search_replace"
                            effect="solid"
                            className="rc-tooltip-custom-class"
                            backgroundColor="#007bff"
                            place="left"
                            html={true}
                            getContent={dataTip => (dataTip ? sanitizeHtml(dataTip) : "")}
                        />

                        <div className="modal-header draggable">
                            <h5 className="modal-title" id="exampleModalLabel">
                                <div className="txt-hed">Find And Replace</div>
                            </h5>
                            <div className="not-draggable">
                                <button type="button" className="close" onClick={onCancel}>
                                    <span aria-hidden="true">
                                        <img src="/img/close.svg" alt="" />
                                    </span>
                                </button>
                            </div>
                        </div>
                        <div className="modal-body region-otr">
                            <div className="col-md-12 formInp search">
                                <input
                                    type="text"
                                    className="form-control"
                                    placeholder="Find"
                                    value={data.search}
                                    onChange={e => onChange("search", e.target.value)}
                                />
                            </div>
                            <div className="col-md-12 formInp search mt-3">
                                <input
                                    type="text"
                                    className="form-control"
                                    placeholder="Replace"
                                    value={data?.replace}
                                    onChange={e => onChange("replace", e.target.value)}
                                />
                            </div>
                            <div className="col-md-12 formInp search mt-3 d-flex flex-wrap align-items-center">
                                <div className="d-flex align-items-center mr-4 text-nowrap">
                                    <div class="img-sq-box seting-type checkbox-container">
                                        <label class="container-checkbox cursor-hand m-0">
                                            <input
                                                type="checkbox"
                                                id="match_case"
                                                checked={data?.match_case}
                                                onChange={e => onChange("match_case", e.target.checked)}
                                            />
                                            <span class="checkmark "></span>
                                        </label>
                                    </div>

                                    <label htmlFor="match_case" className="m-0">
                                        Match Case
                                    </label>
                                </div>
                                <div className="d-flex align-items-center mr-4 text-nowrap">
                                    <div class="img-sq-box seting-type checkbox-container">
                                        <label class="container-checkbox cursor-hand m-0">
                                            <input
                                                type="checkbox"
                                                id="match_entire_cell"
                                                checked={data?.match_entire_cell}
                                                onChange={e => onChange("match_entire_cell", e.target.checked)}
                                            />
                                            <span class="checkmark "></span>
                                        </label>
                                    </div>

                                    <label htmlFor="match_entire_cell" className="m-0">
                                        Match Entire Cell
                                    </label>
                                </div>
                                <div className="d-flex align-items-center ml-auto text-nowrap">
                                    <label htmlFor="search_operator" className="m-0 mr-2 d-flex align-items-center">
                                        Condition
                                        <i
                                            className="fas fa-info-circle search-replace-i ml-1"
                                            data-for="search_replace"
                                            data-tip={`<b>OR</b> : ${entityPluralLabelCaps} where at least one selected field contains the find value.
                                                    <br /> <b>AND</b> : ${entityPluralLabelCaps} where every selected field contains the find value.`}
                                        />
                                    </label>
                                    <div
                                        className={hasMultipleFields ? "cursor-pointer" : "cursor-disabled"}
                                        data-for="search_replace"
                                        data-tip={hasMultipleFields ? "" : "Condition is enabled only when more than one field is selected"}
                                    >
                                        <ReactSelect
                                            inputId="search_operator"
                                            classNamePrefix="react-select"
                                            placeholder="Select"
                                            value={searchOperatorOptions.find(e => e.value === (data?.search_operator ?? "OR"))}
                                            options={searchOperatorOptions}
                                            onChange={e => onChange("search_operator", e?.value ?? "OR")}
                                            isDisabled={!hasMultipleFields}
                                            isSearchable={false}
                                            isClearable={false}
                                            menuPlacement="auto"
                                            styles={{
                                                container: base => ({ ...base, width: 100 }),
                                                control: (base, state) => ({
                                                    ...base,
                                                    minHeight: 34,
                                                    borderColor: "#ced4da",
                                                    boxShadow: "none",
                                                    backgroundColor: state.isDisabled ? "#e9ecef" : "#fff",
                                                    cursor: state.isDisabled ? "not-allowed" : "pointer",
                                                    pointerEvents: state.isDisabled ? "none" : "auto"
                                                }),
                                                valueContainer: base => ({ ...base, padding: "0 8px" }),
                                                singleValue: (base, state) => ({ ...base, color: state.isDisabled ? "#6c757d" : base.color }),
                                                indicatorSeparator: () => ({ display: "none" }),
                                                dropdownIndicator: base => ({ ...base, padding: "0 6px" }),
                                                option: base => ({ ...base, cursor: "pointer" })
                                            }}
                                        />
                                    </div>
                                </div>
                            </div>
                            <div className="col-md-12 formInp search mt-3 find-field">
                                <div className="col-md-12 pr-0 field-form pl-2">
                                    <label>
                                        Fields
                                        <i
                                            className="fas fa-info-circle search-replace-i ml-2"
                                            data-for="search_replace"
                                            data-tip={`Only regular fields can be selected (No Dropdown and/or Boolean).
                                                    <br /> For modifying Dropdown and/or Boolean fields, please use Group Edit`}
                                        />
                                    </label>
                                    <ReactSelect
                                        className="form-control p-1"
                                        classNamePrefix="react-select"
                                        placeholder="Select Fields"
                                        isMulti
                                        value={data?.replace_fields}
                                        options={(tableFields ?? [])?.filter(e => !data?.replace_fields?.some(i => i.value === e?.value))}
                                        onChange={e => onChange("replace_fields", e)}
                                        filterOption={filterOption}
                                        isClearable={false}
                                    />
                                </div>
                            </div>
                            <div className="col-md-12 check-otr d-flex checkbox-sec not-draggable">
                                <span className="errorMessage">{errorMessage}</span>
                            </div>
                            <div class="col-md-12 p-0 mt-4 text-right  d-flex col-md-12">
                                <div>
                                    {tableSearch?.find && tableSearch?.find?.trim() === data?.search?.trim() ? (
                                        <button
                                            type="button"
                                            onClick={() => {
                                                setFindParams(prev => ({ ...prev, findParams: null }));
                                            }}
                                            className={`btn btn-primary `}
                                        >
                                            Clear Find
                                        </button>
                                    ) : (
                                        <button
                                            type="button"
                                            onClick={() => {
                                                if (showFindTypeSelection) {
                                                    setShowTypeSelect(true);
                                                } else {
                                                    applyFindParams();
                                                }
                                            }}
                                            className={`${
                                                !data?.search?.length || !data?.replace_fields?.length ? "cursor-disabled" : ""
                                            } btn btn-primary search-replace-find-button `}
                                            disabled={!data?.search?.length || !data?.replace_fields?.length}
                                            data-for="search_replace"
                                            data-tip={
                                                !data?.search?.length
                                                    ? "Please enter the search phrase"
                                                    : !data?.replace_fields?.length
                                                      ? "Please choose the field to find from"
                                                      : ""
                                            }
                                        >
                                            Find {entityPluralLabel}
                                        </button>
                                    )}
                                </div>
                                <div className="tot-btr">
                                    <button
                                        type="button"
                                        onClick={() => {
                                            setIsDeleteModal(true);
                                            findBeforeReplace(
                                                { ...data, replace_fields: data?.replace_fields?.map(e => e?.value), replace: "" },
                                                () => {
                                                    setShowConfirmReplaceModal(true);
                                                }
                                            );
                                        }}
                                        className={`btn btn-danger btnRgion btn-wid mr-1 ${
                                            !data?.search?.length || !data?.replace_fields?.length ? "cursor-disabled" : ""
                                        }`}
                                        data-for="search_replace"
                                        data-tip={
                                            !data?.search?.length
                                                ? "Please enter the search phrase"
                                                : !data?.replace_fields?.length
                                                  ? "Please choose the field to find from"
                                                  : `Remove the Find Phrase from ${entityPluralLabel} in the selected fields`
                                        }
                                        disabled={!data?.search?.length || !data?.replace_fields?.length}
                                    >
                                        {loading && isDeleteModal ? (
                                            <span className="spinner-border spinner-border-sm pl-2" role="status"></span>
                                        ) : (
                                            <i className="far fa-trash-alt" />
                                        )}
                                    </button>
                                    <button type="button" onClick={onCancel} className="btn btn-secondary btnClr col-md-3 mr-1" data-dismiss="modal">
                                        Cancel
                                    </button>

                                    <button
                                        type="button"
                                        onClick={() => {
                                            const hasError = validateData();
                                            if (!hasError) {
                                                findBeforeReplace({ ...data, replace_fields: data?.replace_fields?.map(e => e?.value) }, () =>
                                                    setShowConfirmReplaceModal(true)
                                                );
                                            }
                                        }}
                                        className="btn btn-primary btnRgion btn-wid"
                                    >
                                        {loading && !isDeleteModal ? (
                                            <span className="spinner-border spinner-border-sm pl-2" role="status"></span>
                                        ) : (
                                            "Replace"
                                        )}
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </Draggable>
            {renderConfirmationModal()}
            {renderConfirmationReplaceModal()}
            {renderOnReplaceConfirm()}
        </div>
    );
};

export default SearchAndReplace;
